/**
 * @file
 * Hero video component using a self-hosted HTML5 <video> element.
 */

(function (Drupal, once) {
  "use strict";

  var FALLBACK_CLASS = "hero-video--fallback";
  var LOADING_CLASS = "is-loading";
  var PLAYING_CLASS = "is-playing";
  var CONTROL_FALLBACK_CLASS = "is-fallback";

  // Phones and data-saver visitors get the poster image instead.
  function wantsVideo() {
    var saveData = navigator.connection && navigator.connection.saveData;
    return window.matchMedia("(min-width: 600px)").matches && !saveData;
  }

  function loadVideo(video) {
    if (!video.getAttribute("src") && video.dataset.src) {
      video.src = video.dataset.src;
    }
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  /**
   * Update the control button icon/label based on the video's play state.
   */
  function updateControlButton(video, button) {
    const pauseIcon = button.querySelector(".hero-video__control-icon--pause");
    const playIcon = button.querySelector(".hero-video__control-icon--play");

    if (!video.paused) {
      // Playing: offer to pause.
      pauseIcon.style.display = "inline-block";
      playIcon.style.display = "none";
      button.setAttribute("aria-label", "Pause video");
    } else {
      // Paused: offer to play.
      pauseIcon.style.display = "none";
      playIcon.style.display = "inline-block";
      button.setAttribute("aria-label", "Play video");
    }
  }

  /**
   * Show the poster image in place of the video.
   */
  function enterFallback(element, controlBtn) {
    // Drop the desktop placeholder source so the poster loads.
    var placeholder = element.querySelector("picture source");
    if (placeholder) {
      placeholder.remove();
    }
    element.classList.add(FALLBACK_CLASS);
    // Reveal layout renders the control outside .hero-video.
    controlBtn.classList.add(CONTROL_FALLBACK_CLASS);
  }

  function exitFallback(element, controlBtn) {
    element.classList.remove(FALLBACK_CLASS);
    controlBtn.classList.remove(CONTROL_FALLBACK_CLASS);
  }

  /**
   * Initialize a single hero video player.
   */
  function initializePlayer(element, isFirstVideo) {
    const video = element.querySelector(".hero-video__player");
    let controlBtn = element.querySelector(".hero-video__control");
    if (!controlBtn && video) {
      controlBtn = document.querySelector(
        '.hero-video__control[data-player-id="' + video.id + '"]',
      );
    }

    if (!video || !controlBtn) {
      return;
    }

    const fallbackImage = element.querySelector(".hero-video__fallback-image");

    // Muted is required for autoplay to be allowed by browsers.
    video.muted = true;

    if (fallbackImage) {
      fallbackImage.addEventListener("error", function () {
        // Avoid a broken-image icon.
        fallbackImage.hidden = true;
      });
    }

    if (isFirstVideo && !prefersReducedMotion() && wantsVideo()) {
      // Fade in on the first frame instead of popping in.
      element.classList.add(LOADING_CLASS);
      // Start after the page's own assets so the video doesn't compete.
      var start = function () {
        loadVideo(video);
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(function () {
            // Show poster image if autoplay fails
            element.classList.remove(LOADING_CLASS);
            enterFallback(element, controlBtn);
            updateControlButton(video, controlBtn);
          });
        }
      };
      if (document.readyState === "complete") {
        start();
      } else {
        window.addEventListener("load", start, { once: true });
      }
    } else if (isFirstVideo && prefersReducedMotion()) {
      // Reduced motion: don't autoplay.
      video.pause();
      enterFallback(element, controlBtn);
    } else {
      // Phones, data saver and later hero videos wait for the play control.
      video.pause();
    }

    video.addEventListener("playing", function () {
      element.classList.remove(LOADING_CLASS);
      element.classList.add(PLAYING_CLASS);
      exitFallback(element, controlBtn);
    });

    video.addEventListener("play", function () {
      updateControlButton(video, controlBtn);
    });
    video.addEventListener("pause", function () {
      updateControlButton(video, controlBtn);
    });
    video.addEventListener("error", function () {
      element.classList.remove(LOADING_CLASS);
      element.classList.add("has-error");
      // Nothing to play.
      enterFallback(element, controlBtn);
      controlBtn.hidden = true;
      console.error("Failed to load hero video.");
    });

    controlBtn.addEventListener("click", function () {
      if (video.paused) {
        loadVideo(video);
        video.play();
      } else {
        video.pause();
      }
    });

    updateControlButton(video, controlBtn);
  }

  /**
   * Drupal behavior for hero video.
   */
  Drupal.behaviors.umsiHeroVideo = {
    attach: function (context) {
      // Find all hero video elements that haven't been initialized.
      const heroVideos = once("umsiHeroVideo", ".hero-video", context);

      if (heroVideos.length === 0) {
        return;
      }

      // All hero videos in the context, used to determine the first one.
      const allHeroVideos = context.querySelectorAll(".hero-video");

      heroVideos.forEach(function (element) {
        const isFirstVideo = Array.from(allHeroVideos).indexOf(element) === 0;
        initializePlayer(element, isFirstVideo);
      });
    },
  };
})(Drupal, once);
